import { Star, Quote, ArrowUpRight } from "lucide-react";
import { REVIEWS, GOOGLE_REVIEWS_URL } from "./data";
import { Reveal } from "./motion";

const GoogleG = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z" />
    <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z" />
  </svg>
);

const Stars = ({ n }) => (
  <div className="flex gap-1" aria-label={`${n} star rating`}>
    {Array.from({ length: n }).map((_, i) => (
      <Star key={i} size={15} className="text-[#fcc41b] fill-[#fcc41b]" />
    ))}
  </div>
);

const ReviewCard = ({ r, i, featured }) => (
  <Reveal delay={i * 0.07} className={featured ? "lg:col-span-2 lg:row-span-2" : ""}>
    <figure
      data-testid={`review-card-${i}`}
      className="relative h-full rounded-3xl border border-[#262626] bg-[#0d0d0d] p-8 lg:p-10 flex flex-col gap-5 hover:border-[#fcc41b]/40 transition-colors duration-500"
    >
      <Quote size={featured ? 34 : 24} className="text-[#fcc41b]/60" />
      <Stars n={r.stars} />
      <blockquote className={`text-zinc-300 leading-relaxed flex-1 ${featured ? "text-lg lg:text-xl" : "text-base"}`}>
        “{r.text}”
      </blockquote>
      <figcaption className="flex items-center gap-3 mt-auto">
        <span className="w-10 h-10 rounded-full bg-[#fcc41b] text-black font-display font-bold flex items-center justify-center text-sm">
          {r.name.split(" ").map((w) => w[0]).join("")}
        </span>
        <span>
          <span className="block font-display font-semibold text-white text-sm">{r.name}</span>
          <span className="block font-mono-eng text-[11px] tracking-wider text-zinc-500">{r.source}</span>
        </span>
      </figcaption>
    </figure>
  </Reveal>
);

export const Reviews = () => (
  <section id="reviews" data-testid="reviews-section" className="relative py-28 lg:py-40 bg-[#010101]">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
        <div>
          <Reveal><p className="text-xs tracking-[0.3em] uppercase text-[#fcc41b] font-display font-medium mb-5">What our customers say</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-semibold tracking-tighter leading-[0.95] text-white" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)" }}>
              Trusted on roofs<br />across Australia.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-reviews-badge"
            className="group inline-flex items-center gap-4 rounded-2xl border border-[#262626] bg-[#0d0d0d] px-6 py-5 hover:border-[#fcc41b]/40 transition-colors duration-300"
          >
            <GoogleG size={28} />
            <span>
              <span className="flex items-center gap-2">
                <Stars n={5} />
                <span className="font-display font-semibold text-white text-sm">Rated on Google</span>
              </span>
              <span className="mt-1 flex items-center gap-1.5 text-[#fcc41b] font-display text-sm">
                Read &amp; leave a review
                <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </span>
            </span>
          </a>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:auto-rows-fr">
        {REVIEWS.map((r, i) => (
          <ReviewCard key={r.name} r={r} i={i} featured={!!r.featured} />
        ))}
      </div>
    </div>
  </section>
);
